import React from "react";
import styled from "styled-components";
import { MIN_SCALE, MAX_SCALE } from "../hooks/useImageZoom";

/**
 * 画像拡縮の操作 UI（縮小 / リセット / 拡大 / 位置リセット + 操作ヒント）。
 * ZoomableImageModal と ImageCompareModal の両方で使う共通プレゼンテーション。
 */

const Controls = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.8);
  padding: 12px 16px;
  border-radius: 20px;
  z-index: 1001;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Hints = styled.div`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  line-height: 1.3;
`;

const Button = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 6px 12px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
  }
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// 画像クリックに割り当てる動作の種類。下部ヒントを切り替えるために使う。
// - "switch": 比較モーダルの「切替」ビュー。クリックで baseline/current を切替（#215）。
// - "none": それ以外（単体 Lightbox / 比較モーダルの Sidebyside / Diff）。クリックは反応なし。
//   tfact_user_request#218 でクリック / ダブルクリック拡大を廃止し、"zoom" は撤去。
export type ZoomClickAction = "switch" | "none";

export interface ZoomControlsProps {
  scale: number;
  position: { x: number; y: number };
  onZoomIn: (e: React.MouseEvent) => void;
  onZoomOut: (e: React.MouseEvent) => void;
  onResetZoom: (e: React.MouseEvent) => void;
  onResetPosition: (e: React.MouseEvent) => void;
  clickAction?: ZoomClickAction;
}

const buildHint = (action: ZoomClickAction): string => {
  const common = "ホイール: 拡大縮小 | ドラッグ: 移動 | ESC: 閉じる";
  switch (action) {
    case "switch":
      return `💡 クリック: Baseline/Current 切替 | ${common}`;
    case "none":
    default:
      return `💡 ${common}`;
  }
};

export const ZoomControls: React.FC<ZoomControlsProps> = ({
  scale,
  position,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onResetPosition,
  clickAction = "none",
}) => (
  <Controls onClick={(e) => e.stopPropagation()}>
    <ButtonRow>
      <Button onClick={onZoomOut} disabled={scale <= MIN_SCALE}>
        縮小 (-0.25)
      </Button>
      <Button onClick={onResetZoom}>
        リセット ({Math.round(scale * 100)}%)
      </Button>
      <Button onClick={onZoomIn} disabled={scale >= MAX_SCALE}>
        拡大 (+0.25)
      </Button>
      <Button
        onClick={onResetPosition}
        disabled={position.x === 0 && position.y === 0}
      >
        位置リセット
      </Button>
    </ButtonRow>
    <Hints>{buildHint(clickAction)}</Hints>
  </Controls>
);
